<script setup lang="ts">
  // Native Help tab: 5 guided accordion flows (desktop help-flow visual
  // language) computed over the native data model, plus the read-only
  // diagnostics block. Flow 2 picks a DESTINATION (dumb-friendly: "my
  // comanda prints weird") but the chosen codepage lands on the assigned
  // PRINTER's settings — hardware truth is per-printer.
  import { computed, getCurrentInstance, ref } from 'vue';
  import { Capacitor } from '@capacitor/core';
  import { useUi } from '@/composition/use-ui';
  import { useModal } from '@/composition/use-modal';
  import { useSupportReport } from '@/composition/use-support-report';
  import { AlertType } from '@/interfaces/IAlert';
  import { CODEPAGE_CYCLE } from '@/lib/print/test-ticket';
  import { IPrinter } from '@/interfaces/IPrinter';
  import { IEscposPrinterSettings } from '@/lib/print/printer-settings';
  import { PrinterCutMode } from '@/stores/printing-v2.config';
  import {
    EscPosPrinter,
    PrinterCapabilities,
  } from '@/lib/capacitor/EscPosPrinter';
  import {
    INativePrinterCard,
    TNativeDestination,
    TNativeHelpFlow,
    nativeCardStatusUi,
    nativeTransportLabelKey,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiListbox, {
    IPosUiListboxItem,
  } from '@/components/pos-v2/ui/PosUiListbox.vue';
  import V2NativePrinterSetupWizard from './wizard/V2NativePrinterSetupWizard.vue';

  const props = defineProps<{
    /** Flow to auto-open on mount (e.g. jumping here from the offline hint). */
    initialFlow?: TNativeHelpFlow | null;
  }>();

  const ui = useUi();
  const modals = useModal();
  const nativeUi = useNativePrintingUi();
  const tests = nativeUi.tests;

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const openFlow = ref<TNativeHelpFlow | null>(props.initialFlow ?? null);

  function toggleFlow(flow: TNativeHelpFlow) {
    openFlow.value = openFlow.value === flow ? null : flow;
  }

  const anyTesting = computed(() => tests.testingPrinterId.value !== null);

  // #region flow 1: no imprime nada (printer-oriented, transport-aware)

  /** Concrete (non-ghost) printers only — a ghost can't be probed/tested. */
  const concreteCards = computed(() =>
    nativeUi.printerCards.value.filter((c) => c.printer !== null)
  );

  const printerItems = computed<IPosUiListboxItem[]>(() =>
    concreteCards.value.map((c) => ({ id: c.name, label: c.name }))
  );

  const noPrintName = ref<string | null>(null);
  const noPrintCard = computed<INativePrinterCard | null>(
    () =>
      concreteCards.value.find((c) => c.name === noPrintName.value) ?? null
  );
  const noPrintKind = computed(() =>
    noPrintCard.value?.printer
      ? nativeUi.resolveTestKind(noPrintCard.value.printer)
      : null
  );

  const probing = ref(false);
  const probeResult = ref<'ok' | 'fail' | null>(null);

  async function probeNoPrint() {
    const config = noPrintCard.value?.networkConfig;
    if (!config || probing.value) return;
    probing.value = true;
    probeResult.value = null;
    try {
      const reachable = await nativeUi.probeOne(config);
      probeResult.value = reachable ? 'ok' : 'fail';
    } finally {
      probing.value = false;
    }
  }

  async function testNoPrint(kindOverride?: 'escpos' | 'zpl') {
    const printer = noPrintCard.value?.printer;
    if (!printer) return;
    await nativeUi.runPrinterTest(printer, kindOverride);
  }

  function reloadInteractive() {
    void nativeUi.reload(true);
  }

  // #endregion

  // #region flow 2: símbolos raros (DESTINATION-oriented — encoding is
  // per-destination natively; Etiquetas excluded, ZPL has no codepages)

  /** 'ticket' or `comanda:${stationId}` (listbox values are plain strings). */
  const symbolsDestination = ref<string | null>(null);
  const samplesPrinted = ref(false);

  const destinationItems = computed<IPosUiListboxItem[]>(() => [
    { id: 'ticket', label: i18nT('printingV2.destTicket') },
    ...nativeUi.restaurantStore.printStations.map((station) => ({
      id: `comanda:${station.stationId}`,
      label: station.name,
    })),
  ]);

  /** The picked destination's assigned CONCRETE printer (null = ghost/none). */
  const symbolsPrinter = computed<IPrinter | null>(() => {
    const dest = symbolsDestination.value;
    if (!dest) return null;
    const name =
      dest === 'ticket'
        ? nativeUi.assignedNameOf('ticket')
        : nativeUi.assignedNameOf('comanda', Number(dest.split(':')[1]));
    if (!name) return null;
    return concreteCards.value.find((c) => c.name === name)?.printer ?? null;
  });

  async function printSamples() {
    if (!symbolsPrinter.value) return;
    const sent = await tests.printCodepageSamples(symbolsPrinter.value);
    if (sent) samplesPrinted.value = true;
  }

  /**
   * Merge-writes an ESC/POS settings patch onto one printer's persisted
   * entry (type defaults to escpos when unset — an untyped entry is an
   * ESC/POS printer by definition here: ZPL printers have no codepage or
   * cut). setPrinterSettings persists AND dual-writes the legacy
   * destination encodings, so old builds keep printing correctly.
   */
  function writeEscposPatch(
    name: string,
    patch: Partial<IEscposPrinterSettings>
  ) {
    const existing = nativeUi.store.printerSettings[name];
    nativeUi.store.setPrinterSettings(name, {
      ...existing,
      type: existing?.type ?? 'escpos',
      escpos: { ...existing?.escpos, ...patch },
    });
  }

  function pickSample(index: number) {
    const printer = symbolsPrinter.value;
    if (!printer) return;
    // Per-PRINTER write (desktop parity): the sample was printed by the
    // assigned printer, so the readable codepage is that printer's truth —
    // every destination it serves picks it up through printerSettingsOf.
    writeEscposPatch(printer.name, { codepage: CODEPAGE_CYCLE[index] });
    samplesPrinted.value = false;
    ui.showToast(AlertType.Success, i18nT('printingV2.helpCodepageSaved'));
  }

  // #endregion

  // #region flow 3: corta mal (per-printer cutMode, desktop blueprint)

  const cutPrinterName = ref<string | null>(null);
  const switchingCutMode = ref(false);

  /** ESC/POS-capable concrete printers (zpl-typed ones don't cut tickets). */
  const cutPrinterItems = computed<IPosUiListboxItem[]>(() =>
    concreteCards.value
      .filter((c) => nativeUi.store.printerSettings[c.name]?.type !== 'zpl')
      .map((c) => ({ id: c.name, label: c.name }))
  );

  const cutPrinter = computed<IPrinter | null>(
    () =>
      concreteCards.value.find((c) => c.name === cutPrinterName.value)
        ?.printer ?? null
  );

  const cutModeLabelKey = computed(() => {
    const name = cutPrinterName.value;
    const mode = name
      ? nativeUi.store.printerSettings[name]?.escpos?.cutMode
      : undefined;
    return (mode ?? 'modern') === 'legacy'
      ? 'printingV2.helpCutModeLegacy'
      : 'printingV2.helpCutModeModern';
  });

  async function testCut() {
    const printer = cutPrinter.value;
    if (!printer) return;
    await tests.testEscPos(printer, nativeUi.settingsForPrinter(printer));
  }

  /** Flips the printer's cut mode, persists it and prints a test with it. */
  async function switchCutModeAndTest() {
    const printer = cutPrinter.value;
    if (!printer || switchingCutMode.value) return;
    const nextMode: PrinterCutMode =
      (nativeUi.store.printerSettings[printer.name]?.escpos?.cutMode ??
        'modern') === 'modern'
        ? 'legacy'
        : 'modern';
    switchingCutMode.value = true;
    try {
      // Synchronous persisted write: the test below resolves settings at
      // print time, so it demonstrates nextMode without the override
      // plumbing the desktop needs for its async commit → projection.
      writeEscposPatch(printer.name, { cutMode: nextMode });
      const sent = await tests.testEscPos(
        printer,
        nativeUi.settingsForPrinter(printer)
      );
      if (sent) {
        ui.showToast(
          AlertType.Success,
          i18nT('printingV2.helpCutModeSwitched')
        );
      }
    } finally {
      switchingCutMode.value = false;
    }
  }

  // #endregion

  // #region flow 4: red — CTA to the setup wizard

  function openWizard() {
    modals.show(
      V2NativePrinterSetupWizard,
      {},
      {
        adaptive: true,
        height: 'auto',
        width: 720,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-native-printer-setup-wizard',
      },
      {}
    );
  }

  // #endregion

  // #region diagnostics (read-only, from existing stores + plugin caps)

  const diagnosticsOpen = ref(false);

  const capabilities = ref<PrinterCapabilities | null>(null);
  const capabilitiesFailed = ref(false);

  /** Lazy one-shot: only fetched when the block is first opened. */
  async function loadCapabilities() {
    if (capabilities.value || capabilitiesFailed.value) return;
    try {
      capabilities.value = await EscPosPrinter.getCapabilities();
    } catch (e) {
      // Old native binary without getCapabilities (or web fallback):
      // render the "unavailable" line instead of breaking the block.
      console.warn('[NativePrinting] getCapabilities unavailable', e);
      capabilitiesFailed.value = true;
    }
  }

  function toggleDiagnostics() {
    diagnosticsOpen.value = !diagnosticsOpen.value;
    if (diagnosticsOpen.value) void loadCapabilities();
  }

  const capabilitiesDetail = computed(() => {
    if (!capabilities.value) return '';
    return [
      ...capabilities.value.transports.map((t) =>
        i18nT(
          nativeTransportLabelKey(t === 'network' ? 'tcp' : t)
        )
      ),
      ...capabilities.value.features,
    ].join(' · ');
  });

  interface IDiagPrinterLine {
    key: string;
    name: string;
    transportKey: string | null;
    statusKey: string | null;
    statusColor: 'success' | 'error' | 'info' | 'neutral';
  }

  const diagPrinters = computed<IDiagPrinterLine[]>(() =>
    nativeUi.printerCards.value.map((card) => {
      const status = nativeCardStatusUi(card);
      return {
        key: card.key,
        name: card.name,
        transportKey: card.isGhost
          ? null
          : nativeTransportLabelKey(card.transport),
        statusKey: status?.labelKey ?? null,
        statusColor: status?.color ?? 'neutral',
      };
    })
  );

  interface IDiagSlotLine {
    key: string;
    slotLabel: string;
    printerName: string | null;
    chip: ReturnType<typeof nativeUi.slotHealthChipOf>;
  }

  const diagSlots = computed<IDiagSlotLine[]>(() => {
    const lines: IDiagSlotLine[] = [];
    function push(
      key: string,
      slotLabel: string,
      destination: TNativeDestination,
      stationId?: number
    ) {
      const name = nativeUi.assignedNameOf(destination, stationId);
      lines.push({
        key,
        slotLabel,
        printerName: name,
        chip: nativeUi.slotHealthChipOf(destination, name),
      });
    }
    push('ticket', i18nT('printingV2.destTicket'), 'ticket');
    for (const station of nativeUi.restaurantStore.printStations) {
      push(
        `comanda:${station.stationId}`,
        station.name,
        'comanda',
        station.stationId
      );
    }
    push('sticker', i18nT('printingV2.destSticker'), 'sticker');
    return lines;
  });

  // #endregion

  // #region support report

  const supportReport = useSupportReport();
  const isNativeShare = Capacitor.isNativePlatform();

  // #endregion
</script>

<template>
  <div class="v2-native-help-tab">
    <!-- 1. No imprime nada -->
    <div class="help-flow" :class="{ open: openFlow === 'noprint' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('noprint')"
      >
        <PosUiIcon name="printer-off" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.helpNoPrintTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'noprint' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'noprint'" class="help-flow-body">
        <div class="help-controls">
          <PosUiListbox
            :value="noPrintName"
            :items="printerItems"
            :placeholder="i18nT('printingV2.helpSelectPrinter')"
            class="help-printer-select"
            @input="
              noPrintName = $event === null ? null : String($event);
              probeResult = null;
            "
          />
        </div>

        <template v-if="noPrintCard">
          <!-- Network: probe + result + subnet tips -->
          <template v-if="noPrintCard.networkConfig">
            <ol class="help-steps">
              <li>{{ $t('printingV2.nativeHelpNetStep1') }}</li>
              <li>{{ $t('printingV2.nativeHelpNetStep2') }}</li>
            </ol>
            <div class="help-controls">
              <PosUiButton
                color="neutral"
                variant="outline"
                size="sm"
                :loading="probing"
                @click="probeNoPrint"
              >
                {{ $t('printingV2.helpProbe') }}
              </PosUiButton>
            </div>
            <div v-if="probeResult === 'ok'" class="help-result is-ok">
              <PosUiIcon name="check-circle" class="help-result-icon" />
              {{ $t('printingV2.helpProbeOk') }}
            </div>
            <div v-else-if="probeResult === 'fail'" class="help-result is-fail">
              <PosUiIcon name="close-circle" class="help-result-icon" />
              {{ $t('printingV2.helpProbeFail') }}
            </div>
          </template>

          <!-- USB: cable/permission checklist + reload -->
          <template v-else-if="noPrintCard.transport === 'usb'">
            <ol class="help-steps">
              <li>{{ $t('printingV2.nativeHelpUsbStep1') }}</li>
              <li>{{ $t('printingV2.nativeHelpUsbStep2') }}</li>
              <li>{{ $t('printingV2.nativeHelpUsbStep3') }}</li>
            </ol>
            <div class="help-controls">
              <PosUiButton
                color="neutral"
                variant="outline"
                size="sm"
                icon="reload"
                :loading="nativeUi.reloading.value"
                @click="reloadInteractive"
              >
                {{ $t('printingV2.refresh') }}
              </PosUiButton>
            </div>
          </template>

          <!-- Bluetooth: pairing checklist + interactive reload -->
          <template v-else>
            <ol class="help-steps">
              <li>{{ $t('printingV2.nativeBtGuide1') }}</li>
              <li>{{ $t('printingV2.nativeBtGuide2') }}</li>
              <li>{{ $t('printingV2.nativeBtGuide3') }}</li>
            </ol>
            <div class="help-controls">
              <PosUiButton
                color="neutral"
                variant="outline"
                size="sm"
                icon="reload"
                :loading="nativeUi.reloading.value"
                @click="reloadInteractive"
              >
                {{ $t('printingV2.refresh') }}
              </PosUiButton>
            </div>
          </template>

          <!-- Honest test: chooser when the printer is unassigned -->
          <div class="help-controls">
            <template v-if="noPrintKind === 'ambiguous'">
              <PosUiButton
                color="primary"
                variant="soft"
                size="sm"
                icon="receipt"
                :disabled="anyTesting"
                @click="testNoPrint('escpos')"
              >
                {{ $t('printingV2.nativeTestAsTicket') }}
              </PosUiButton>
              <PosUiButton
                color="primary"
                variant="soft"
                size="sm"
                icon="tag-outline"
                :disabled="anyTesting"
                @click="testNoPrint('zpl')"
              >
                {{ $t('printingV2.nativeTestAsLabel') }}
              </PosUiButton>
            </template>
            <PosUiButton
              v-else
              color="primary"
              variant="soft"
              size="sm"
              :loading="
                tests.testingPrinterId.value === noPrintCard.printer?.id
              "
              :disabled="anyTesting"
              @click="testNoPrint()"
            >
              {{ $t('printingV2.testPrintButton') }}
            </PosUiButton>
          </div>
        </template>
      </div>
    </div>

    <!-- 2. Imprime símbolos raros -->
    <div class="help-flow" :class="{ open: openFlow === 'symbols' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('symbols')"
      >
        <PosUiIcon name="format-letter-case" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.helpSymbolsTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'symbols' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'symbols'" class="help-flow-body">
        <p class="help-text">{{ $t('printingV2.nativeHelpSymbolsIntro') }}</p>
        <div class="help-controls">
          <PosUiListbox
            :value="symbolsDestination"
            :items="destinationItems"
            :placeholder="i18nT('printingV2.nativeHelpSelectDestination')"
            class="help-printer-select"
            @input="
              symbolsDestination = $event === null ? null : String($event);
              samplesPrinted = false;
            "
          />
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            :loading="
              symbolsPrinter
                ? tests.testingPrinterId.value === symbolsPrinter.id
                : false
            "
            :disabled="!symbolsPrinter || anyTesting"
            @click="printSamples"
          >
            {{ $t('printingV2.helpPrintSamples') }}
          </PosUiButton>
        </div>
        <div
          v-if="symbolsDestination && !symbolsPrinter"
          class="help-note"
        >
          <PosUiIcon name="printer-off" class="help-note-icon" />
          {{ $t('printingV2.nativeHelpSymbolsNoPrinter') }}
        </div>
        <template v-if="samplesPrinted">
          <p class="help-text">{{ $t('printingV2.helpPickSample') }}</p>
          <div class="help-controls">
            <PosUiButton
              v-for="(codepage, index) in CODEPAGE_CYCLE"
              :key="codepage"
              color="neutral"
              variant="outline"
              size="sm"
              @click="pickSample(index)"
            >
              {{ $t('printingV2.helpSampleN', { n: index + 1 }) }}
            </PosUiButton>
          </div>
        </template>
        <p class="help-text is-muted">
          {{ $t('printingV2.nativeHelpSymbolsMore') }}
        </p>
      </div>
    </div>

    <!-- 3. Corta mal / no corta -->
    <div class="help-flow" :class="{ open: openFlow === 'cut' }">
      <button class="help-flow-header" type="button" @click="toggleFlow('cut')">
        <PosUiIcon name="content-cut" class="help-flow-icon" />
        <span class="help-flow-title">{{ $t('printingV2.helpCutTitle') }}</span>
        <PosUiIcon
          :name="openFlow === 'cut' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'cut'" class="help-flow-body">
        <p class="help-text">{{ $t('printingV2.helpCutBody') }}</p>
        <div class="help-controls">
          <PosUiListbox
            :value="cutPrinterName"
            :items="cutPrinterItems"
            :placeholder="i18nT('printingV2.helpSelectPrinter')"
            class="help-printer-select"
            @input="cutPrinterName = $event === null ? null : String($event)"
          />
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            :loading="
              cutPrinter ? tests.testingPrinterId.value === cutPrinter.id : false
            "
            :disabled="!cutPrinter || anyTesting"
            @click="testCut"
          >
            {{ $t('printingV2.testPrintButton') }}
          </PosUiButton>
        </div>
        <template v-if="cutPrinter">
          <p class="help-text">{{ $t('printingV2.helpCutSwitchIntro') }}</p>
          <div class="help-controls">
            <span class="help-cut-mode">
              {{ $t('printingV2.helpCutModeLabel') }}
              <strong>{{ $t(cutModeLabelKey) }}</strong>
            </span>
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              :loading="switchingCutMode"
              :disabled="anyTesting"
              @click="switchCutModeAndTest"
            >
              {{ $t('printingV2.helpCutSwitchButton') }}
            </PosUiButton>
          </div>
        </template>
      </div>
    </div>

    <!-- 4. No encuentro mi impresora Bluetooth -->
    <div class="help-flow" :class="{ open: openFlow === 'bt' }">
      <button class="help-flow-header" type="button" @click="toggleFlow('bt')">
        <PosUiIcon name="bluetooth" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.nativeHelpBtTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'bt' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'bt'" class="help-flow-body">
        <ol class="help-steps">
          <li>{{ $t('printingV2.nativeBtGuide1') }}</li>
          <li>{{ $t('printingV2.nativeBtGuide2') }}</li>
          <li>{{ $t('printingV2.nativeBtGuide3') }}</li>
        </ol>
        <div class="help-controls">
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            icon="reload"
            :loading="nativeUi.reloading.value"
            @click="reloadInteractive"
          >
            {{ $t('printingV2.refresh') }}
          </PosUiButton>
        </div>
      </div>
    </div>

    <!-- 5. No encuentro mi impresora de red -->
    <div class="help-flow" :class="{ open: openFlow === 'network' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('network')"
      >
        <PosUiIcon name="lan-disconnect" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.nativeHelpNetTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'network' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'network'" class="help-flow-body">
        <ol class="help-steps">
          <li>{{ $t('printingV2.nativeHelpNetStep1') }}</li>
          <li>{{ $t('printingV2.nativeHelpNetStep2') }}</li>
          <li>{{ $t('printingV2.nativeHelpNetStep3') }}</li>
        </ol>
        <div class="help-controls">
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            icon="plus"
            @click="openWizard"
          >
            {{ $t('printingV2.addPrinter') }}
          </PosUiButton>
        </div>
      </div>
    </div>

    <!-- Diagnostics (read-only, collapsible) -->
    <div class="help-flow" :class="{ open: diagnosticsOpen }">
      <button class="help-flow-header" type="button" @click="toggleDiagnostics">
        <PosUiIcon name="stethoscope" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.diagnosticsTitle')
        }}</span>
        <PosUiIcon
          :name="diagnosticsOpen ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="diagnosticsOpen" class="help-flow-body">
        <div class="diag-block">
          <div class="diag-block-title">
            {{ $t('printingV2.diagBridge') }}
          </div>
          <div v-if="capabilities" class="diag-line">
            <span class="diag-line-main">v{{ capabilities.nativeVersion }}</span>
            <span class="diag-line-detail">{{ capabilitiesDetail }}</span>
          </div>
          <!-- Loading vs old-binary-without-getCapabilities: neither means
               the plugin is disconnected, so never claim "Sin conexión". -->
          <div v-else class="diag-line">
            <span class="diag-line-detail">{{
              capabilitiesFailed ? '—' : $t('printingV2.nativeProbing')
            }}</span>
          </div>
        </div>

        <div class="diag-block">
          <div class="diag-block-title">
            {{ $t('printingV2.diagPrinters') }}
          </div>
          <div v-if="!diagPrinters.length" class="diag-line">
            <span class="diag-line-detail">{{
              $t('printingV2.diagNoPrinters')
            }}</span>
          </div>
          <div v-for="line in diagPrinters" :key="line.key" class="diag-line">
            <span class="diag-line-main">{{ line.name }}</span>
            <span v-if="line.transportKey" class="diag-line-detail">{{
              $t(line.transportKey)
            }}</span>
            <PosUiBadge
              v-if="line.statusKey"
              :color="line.statusColor"
              variant="soft"
              size="xs"
            >
              {{ $t(line.statusKey) }}
            </PosUiBadge>
          </div>
        </div>

        <div class="diag-block">
          <div class="diag-block-title">
            {{ $t('printingV2.diagSlots') }}
          </div>
          <div v-for="line in diagSlots" :key="line.key" class="diag-line">
            <span class="diag-line-main">{{ line.slotLabel }}</span>
            <template v-if="line.printerName">
              <span class="diag-line-detail">→ {{ line.printerName }}</span>
              <PosUiBadge :color="line.chip.color" variant="soft" size="xs">
                {{ $t(line.chip.labelKey) }}
              </PosUiBadge>
            </template>
            <span v-else class="diag-line-detail">{{
              $t('printingV2.healthUnassigned')
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Support report -->
    <div class="help-support">
      <div class="help-support-text">
        <span class="help-support-title">{{
          $t('printingV2.supportReportTitle')
        }}</span>
        <span class="help-support-body">{{
          $t('printingV2.supportReportBody')
        }}</span>
      </div>
      <div class="help-support-actions">
        <template v-if="isNativeShare">
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            icon="share-variant"
            :loading="supportReport.isBuilding.value"
            @click="supportReport.shareReport('native_printing_help')"
          >
            {{ $t('printingV2.supportReportShare') }}
          </PosUiButton>
        </template>
        <template v-else>
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            icon="content-copy"
            :loading="supportReport.isBuilding.value"
            @click="supportReport.copyReport('native_printing_help')"
          >
            {{ $t('printingV2.supportReportCopy') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            icon="download"
            :loading="supportReport.isBuilding.value"
            @click="supportReport.downloadReport('native_printing_help')"
          >
            {{ $t('printingV2.supportReportDownload') }}
          </PosUiButton>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-help-tab {
    @apply tw-flex tw-flex-col tw-gap-2;

    .help-flow {
      @apply tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

      &.open {
        @apply tw-border-neutral-300;
      }

      .help-flow-header {
        @apply tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-left tw-transition-colors hover:tw-bg-neutral-50;

        .help-flow-icon {
          @apply tw-flex-none tw-text-xl tw-text-neutral-400;
        }

        .help-flow-title {
          @apply tw-flex-1 tw-text-sm tw-font-semibold tw-text-neutral-800;
        }

        .help-flow-chevron {
          @apply tw-text-lg tw-text-neutral-400;
        }
      }

      .help-flow-body {
        @apply tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-neutral-100 tw-px-4 tw-py-3.5;

        .help-text {
          @apply tw-m-0 tw-text-sm tw-text-neutral-600;

          &.is-muted {
            @apply tw-text-xs tw-text-neutral-400;
          }
        }

        .help-steps {
          @apply tw-m-0 tw-flex tw-flex-col tw-gap-1.5 tw-pl-5 tw-text-sm tw-text-neutral-600;
        }

        .help-controls {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

          .help-printer-select {
            @apply tw-w-full tw-max-w-[260px];
          }

          .help-cut-mode {
            @apply tw-text-sm tw-text-neutral-600;

            strong {
              @apply tw-font-semibold tw-text-neutral-800;
            }
          }
        }

        .help-result {
          @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm;

          &.is-ok {
            @apply tw-bg-emerald-50 tw-text-emerald-700;
          }

          &.is-fail {
            @apply tw-bg-red-50 tw-text-red-700;
          }

          .help-result-icon {
            @apply tw-text-base;
          }
        }

        .help-note {
          @apply tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-xs tw-text-neutral-500;

          .help-note-icon {
            @apply tw-mt-0.5 tw-flex-none tw-text-base tw-text-neutral-400;
          }
        }

        .diag-block {
          @apply tw-flex tw-flex-col tw-gap-1;

          .diag-block-title {
            @apply tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-400;
          }

          .diag-line {
            @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm;

            .diag-line-main {
              @apply tw-font-medium tw-text-neutral-800;
            }

            .diag-line-detail {
              @apply tw-text-xs tw-text-neutral-500;
            }
          }
        }
      }
    }

    .help-support {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-dashed tw-border-neutral-200 tw-bg-neutral-50 tw-px-4 tw-py-3.5;

      .help-support-text {
        @apply tw-flex tw-min-w-[200px] tw-flex-1 tw-flex-col tw-gap-0.5;

        .help-support-title {
          @apply tw-text-sm tw-font-semibold tw-text-neutral-800;
        }

        .help-support-body {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .help-support-actions {
        @apply tw-flex tw-flex-none tw-items-center tw-gap-2;
      }
    }
  }
</style>
